import { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Form from "rc-field-form";
import Field from "../../../../../components/Field";
import Button from "../../../../../components/Button";
import Text from "../../../../../components/Inputs/Text";
import Select from "../../../../../components/Inputs/Select";
import { ProfileContext } from "../../../context";
import { useCustomFields } from "../../../../Settings/CustomFields/context";
import { useProfileApi } from "../../../../../utils/api/profile";
import { usePeopleApi } from "../../../../../utils/api/people";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";

function ChangeCustomFields({ close }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { peopleCustomFields } = useCustomFields();

    const input = useCallback((field) => {
        let Component;
        const placeholder = field.code;
        switch (field.type) {
            case "number":
                Component = <Text type="number" min={0} placeholder={placeholder} />
                break;
            case "email":
                Component = <Text type="email" placeholder={placeholder} />
                break;
            case "select":
                Component = (
                    <Select placeholder={placeholder} allowClear>
                        {field.options.map((option) => (
                            <Select.Option value={option.value} key={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                );
                break;
            case "text":
            default:
                Component = <Text placeholder={placeholder} />
                break;
        }
        return Component
    }, []);

    const rules = useCallback((field) => {
        const values = [
            {
                required: field.isRequired,
                message: t("required-custom-field-type", { code: field.code })
            }
        ];

        if (field.type === "email") {
            values.push({
                type: "email",
                message: t("invalid-email"),
            });
        }

        if (field.type === "number") {
            values.push({
                min: 0,
                message: t("custom-field-bigger-than-0", {
                    code: field.code
                })
            })
        }

        return values;
    }, [t]);

    const { user, submitting, submittingError, submit } =
        useContext(ProfileContext);
    const { changeCustomFields: profileSubmit } = useProfileApi();
    const { changeCustomFields: peopleSubmit } = usePeopleApi();

    const onFinish = useCallback(
        (values) => {
            submit(profileSubmit, peopleSubmit, values, close);
        },
        [submit, profileSubmit, peopleSubmit, close],
    );
    useEffect(() => {
        form.setFieldsValue({ customFields: user?.customFields });
    }, [user, form]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm(
            [],
            submittingError,
        );
        form.setFields(fieldErrors);
    }, [form, submittingError]);

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
        >
            {peopleCustomFields?.length > 0 && (
                peopleCustomFields.map((field) => (
                    <Field
                        label={field.code}
                        name={["customFields", field.code]}
                        rules={rules(field)}
                    >
                        {input(field)}
                    </Field>
                ))
            )}

            <div className="d-flex justify-content-end">
                <Button
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                    onClick={close}
                    disabled={submitting}
                >
                    {t("cancel")}
                </Button>

                <Button
                    color="primary"
                    type="submit"
                    loading={submitting}
                    className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
                >
                    {t("save")}
                </Button>
            </div>
        </Form>
    );
}

export default ChangeCustomFields;
